import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Lock, Users } from "lucide-react";
import { eventImg, fmtDate, fmtTime, ELIGIBILITY_LABEL, img } from "@/lib/community";

export function SectionTitle({ eyebrow, title, em, children, className = "", testId }) {
  return (
    <div className={className} data-testid={testId}>
      {eyebrow && <p className="jc-eyebrow">{eyebrow}</p>}
      <h2 className="jc-display text-4xl sm:text-5xl lg:text-6xl mt-4">
        {title} {em && <em className="italic text-[#A8865A]">{em}</em>}
      </h2>
      {children && <div className="mt-5 text-[#7A6A62] max-w-xl leading-relaxed text-sm md:text-base">{children}</div>}
    </div>
  );
}

export function EventCard({ event, idx = 0 }) {
  const full = event.spots_left === 0;
  return (
    <Link
      to={`/events/${event.id}`}
      data-testid={`event-card-${event.id}`}
      className={`group block jc-fade jc-d${(idx % 4) + 1}`}
    >
      <div className="jc-card-img aspect-[4/5] relative">
        <img src={eventImg(event)} alt={event.title} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="bg-[#FBF8F3]/90 backdrop-blur px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em]">
            {event.category}
          </span>
          {event.eligibility !== "public" && (
            <span className="bg-[#2B2522]/85 text-[#FBF8F3] px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1">
              <Lock className="w-3 h-3" strokeWidth={1.5} /> {ELIGIBILITY_LABEL[event.eligibility]}
            </span>
          )}
        </div>
        {event.status === "cancelled" && (
          <div className="absolute inset-0 bg-[#2B2522]/55 flex items-center justify-center">
            <span className="jc-display italic text-3xl text-[#FBF8F3]">Cancelled</span>
          </div>
        )}
      </div>
      <div className="pt-5">
        <p className="jc-eyebrow">{fmtDate(event.start_at, { weekday: "short", month: "short", day: "numeric" })} · {fmtTime(event.start_at)}</p>
        <h3 className="jc-display text-2xl md:text-3xl mt-2 group-hover:text-[#A8865A] transition-colors">{event.title}</h3>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#7A6A62]">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" strokeWidth={1.25} />{event.location}</span>
          <span className="inline-flex items-center gap-1.5" data-testid={`event-card-spots-${event.id}`}>
            <Users className="w-3.5 h-3.5" strokeWidth={1.25} />
            {full ? "Full · waitlist open" : `${event.spots_left} spots left`}
          </span>
          <span data-testid={`event-card-price-${event.id}`}>{event.price > 0 ? `$${Number(event.price).toFixed(event.price % 1 ? 2 : 0)}` : "Free"}</span>
        </div>
        {event.my_rsvp && (
          <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#A8865A]">
            {event.my_rsvp.status === "confirmed" ? "You're going" : event.my_rsvp.status === "pending_payment" ? "Spot held · payment pending" : "You're on the waitlist"}
          </p>
        )}
      </div>
    </Link>
  );
}

export function BulletinCard({ item, large = false }) {
  return (
    <Link to={`/bulletin/${item.id}`} data-testid={`bulletin-card-${item.id}`} className="group block">
      <div className={`jc-card-img ${large ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
        {item.cover_image ? (
          <img src={img(item.cover_image)} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#F3E1DC] to-[#E8DCCB] flex items-center justify-center">
            <span className="jc-display italic text-3xl text-[#A8865A]">Jenova</span>
          </div>
        )}
      </div>
      <div className="pt-5">
        <div className="flex items-center gap-3">
          <span className="jc-eyebrow">{item.category}</span>
          {item.pinned && <span className="text-[10px] uppercase tracking-[0.2em] text-[#7A6A62]">· Pinned</span>}
          {item.audience !== "public" && <Lock className="w-3 h-3 text-[#A8865A]" strokeWidth={1.5} />}
        </div>
        <h3 className={`jc-display mt-2 group-hover:text-[#A8865A] transition-colors ${large ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"}`}>
          {item.title}
        </h3>
        <p className="mt-3 text-sm text-[#7A6A62] leading-relaxed line-clamp-3">{item.description}</p>
        <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[#7A6A62] inline-flex items-center gap-2">
          <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.25} /> {fmtDate(item.date || item.published_at)}
        </p>
      </div>
    </Link>
  );
}

export function EmptyState({ title, children, testId }) {
  return (
    <div data-testid={testId} className="border border-dashed border-[#E8DCCB] rounded-sm py-20 px-6 text-center bg-[#F5EFE6]/40">
      <p className="jc-display italic text-3xl text-[#A8865A]">{title}</p>
      {children && <p className="mt-3 text-sm text-[#7A6A62] max-w-md mx-auto">{children}</p>}
    </div>
  );
}
